import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faHistory, faEye, faUndo, faClock, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { getQuestionnaireVersions, restoreQuestionnaireVersion } from '../../services/api';

interface Version {
  _id: string;
  version: string;
  createdAt: string;
  createdBy: string;
  changes: string;
  isActive: boolean;
  fullChangelog: Array<{
    version: string;
    date: string;
    changes: string;
    author: string;
  }>;
}

interface VersionHistoryProps {
  templateId: string;
  templateTitle: string;
  currentVersion: string;
  onClose: () => void;
  onVersionRestored: (newTemplate: any) => void;
  onSelectVersion?: (versionId: string) => void;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({
  templateId,
  templateTitle,
  currentVersion,
  onClose,
  onVersionRestored,
  onSelectVersion
}) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState<string | null>(null);

  useEffect(() => {
    loadVersions();
  }, [templateId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const response = await getQuestionnaireVersions(templateId);
      setVersions(response.data);
    } catch (error) {
      console.error('Error loading versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreVersion = async (versionId: string, versionNumber: string) => {
    try {
      setRestoring(versionId);
      const response = await restoreQuestionnaireVersion(templateId, versionId);
      
      // Callback mit neuer Template-Daten
      onVersionRestored(response.data);
      
      // Versionen neu laden
      await loadVersions();
    } catch (error) {
      console.error('Error restoring version:', error);
    } finally {
      setRestoring(null);
    }
  };

  const handleSelectVersion = (versionId: string) => {
    if (onSelectVersion) {
      onSelectVersion(versionId);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getVersionIcon = (version: Version) => {
    if (version.isActive) return '🟢';
    if (version.version === '1.0') return '🆕';
    if (version.changes.includes('Wiederhergestellt')) return '🔄';
    if (version.changes.includes('Dupliziert')) return '📋';
    return '📝';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.01 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.99 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.99 }}
        transition={{ duration: 0.02 }}
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* **KOMPAKTER HEADER** */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-base font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faHistory} className="text-blue-500 mr-2 text-sm" />
              Versionshistorie
            </h2>
            <p className="text-xs text-gray-600 mt-1 truncate">
              {templateTitle} • Aktuelle Version: {currentVersion}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
          >
            <FontAwesomeIcon icon={faTimes} className="text-sm" />
          </button>
        </div>
        
        {/* **KOMPAKTER CONTENT** */}
        <div className="overflow-y-auto max-h-[calc(80vh-80px)]">
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-3"></div>
              <p className="text-xs text-gray-600">Lade Versionshistorie...</p>
            </div>
          ) : (
            <div className="p-4">
              {versions.length > 0 ? (
                <div className="space-y-2">
                  {versions.map((version, index) => (
                    <motion.div
                      key={version._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className={`border rounded-md p-3 transition-all duration-150 ${
                        version.isActive 
                          ? 'border-green-400 bg-green-50 shadow-sm' 
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          {/* **KOMPAKTE VERSION INFO** */}
                          <div className="flex items-center mb-1">
                            <span className="text-sm mr-2">{getVersionIcon(version)}</span>
                            <h3 className="font-medium text-gray-900 text-sm">
                              Version {version.version}
                            </h3>
                            {version.isActive && (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-green-500 text-white rounded-full font-medium">
                                Aktiv
                              </span>
                            )}
                          </div>
                          
                          {/* **KOMPAKTE ZEIT INFO** */}
                          <div className="flex items-center text-xs text-gray-500 mb-1">
                            <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
                            <span>{formatDate(version.createdAt)}</span>
                            <span className="mx-1.5">•</span>
                            <span className="truncate">{version.createdBy}</span>
                          </div>
                          
                          {/* **KOMPAKTE ÄNDERUNGEN** */}
                          <p className="text-xs text-gray-700 mb-2 line-clamp-2">
                            {version.changes}
                          </p>
                          
                          {/* **KOMPAKTE CHANGELOG-DETAILS** */}
                          {version.fullChangelog.length > 1 && (
                            <details className="text-xs text-gray-500">
                              <summary className="cursor-pointer hover:text-gray-700 flex items-center">
                                <FontAwesomeIcon icon={faChevronDown} className="mr-1 text-xs" />
                                {version.fullChangelog.length} Änderungen
                              </summary>
                              <div className="mt-1 pl-3 border-l-2 border-gray-200 space-y-1">
                                {version.fullChangelog.slice().reverse().map((change, idx) => (
                                  <div key={idx} className="text-xs">
                                    <span className="font-medium">v{change.version}</span> 
                                    <span className="text-gray-400 mx-1">•</span>
                                    <span className="text-gray-600">{change.changes}</span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                        
                        {/* **KOMPAKTE BUTTONS** */}
                        <div className="flex items-center space-x-1 ml-3">
                          {onSelectVersion && (
                            <button
                              onClick={() => handleSelectVersion(version._id)}
                              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 font-medium"
                            >
                              <FontAwesomeIcon icon={faEye} className="mr-1" />
                              Öffnen
                            </button>
                          )}
                          
                          {!version.isActive && (
                            <button
                              onClick={() => handleRestoreVersion(version._id, version.version)}
                              disabled={restoring === version._id}
                              className={`px-2 py-1 text-xs rounded font-medium transition-colors ${
                                restoring === version._id
                                  ? 'bg-gray-400 text-white cursor-not-allowed'
                                  : 'bg-orange-500 text-white hover:bg-orange-600'
                              }`}
                            >
                              {restoring === version._id ? (
                                <>
                                  <FontAwesomeIcon icon={faHistory} className="animate-spin mr-1" />
                                  Lädt...
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faUndo} className="mr-1" />
                                  Wiederherstellen
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FontAwesomeIcon icon={faHistory} className="text-2xl mb-2" />
                  <p className="text-sm">Keine Versionshistorie verfügbar</p>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default VersionHistory;